// ─────────────────────────────────────────────────────────────────────────────
// INSTALLER L'APPLICATION — la PREMIÈRE étape du parcours de Mario (N°777-4).
//
// 🔴 CE QUI A ÉTÉ CONSTATÉ : le lien envoyé s'ouvre dans Samsung Internet, ⛔ pas
//    dans la PWA. JJ demande que « le lien mène à l'installation ».
//
// ⚠️ CE QU'ON NE PEUT PAS FAIRE, ET IL FAUT LE DIRE PLUTÔT QUE DE LE PROMETTRE :
//    une page web ⛔ NE PEUT PAS choisir le navigateur qui l'ouvre, ⛔ ni se forcer
//    dans une PWA. C'est l'OS (Android) et le navigateur par défaut qui décident,
//    et aucun en-tête, aucun `<meta>`, aucun script ⛔ ne change ça.
// 🔑 CE QU'ON PEUT FAIRE, et que ce composant fait : rendre l'installation ÉVIDENTE
//    À L'ARRIVÉE, avec le geste EXACT du navigateur utilisé. Une fois installée,
//    l'icône de l'écran d'accueil ouvre le parcours en plein écran, sans barre
//    d'adresse — et c'est ce que Mario veut réellement.
//
// TROIS CHEMINS, parce que les navigateurs ne se comportent pas pareil :
//   · Chrome / Edge Android → événement `beforeinstallprompt` ⇒ bouton NATIF.
//   · Samsung Internet → ⛔ PAS d'événement : l'installation existe mais elle est
//     dans le menu. ⇒ on DONNE le chemin exact du menu, ⛔ on ⛔ ne dit pas
//     « installez l'application » sans expliquer où.
//   · Déjà installé (mode `standalone`) ⇒ ⛔ AUCUNE invite : rien n'est plus
//     agaçant qu'une bannière qui propose ce qui est déjà fait.
//
// ⚠️ `manifest-chantier.json` est substitué au manifest global au montage : sans
//    lui, l'installation créerait un raccourci vers `/` (l'ERP desktop) au lieu du
//    parcours. Le manifest est REMIS à l'identique au démontage — un écran ⛔ ne
//    laisse pas l'application dans un état qu'il a changé pour lui seul.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import { Download, Share2, X } from "lucide-react";
import { Button } from "../../components/ui/button";

const MANIFEST_CHANTIER = "/manifest-chantier.json";

function estInstallee() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)")?.matches
    // ⚠️ iOS n'expose pas `display-mode` de la même façon : `navigator.standalone`.
    || window.navigator?.standalone === true;
}

function navigateur() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  if (/SamsungBrowser/i.test(ua)) return "samsung";
  if (/Edg\//i.test(ua)) return "edge";
  if (/Chrome\//i.test(ua) && !/OPR|Edg/i.test(ua)) return "chrome";
  if (/Firefox/i.test(ua)) return "firefox";
  if (/Safari/i.test(ua)) return "safari";
  return "autre";
}

//: Le geste EXACT, par navigateur. ⛔ Pas un « installez l'app » vague.
const GESTES = {
  samsung: "Menu (☰ en bas à droite) → « Ajouter la page à » → « Écran d'accueil »",
  chrome: "Menu (⋮ en haut à droite) → « Ajouter à l'écran d'accueil »",
  edge: "Menu (…) → « Ajouter au téléphone »",
  firefox: "Menu (⋮) → « Installer »",
  safari: "Partager (⌴) → « Sur l'écran d'accueil »",
  autre: "Menu du navigateur → « Ajouter à l'écran d'accueil »",
};

export default function InvitationPWA() {
  const [invite, setInvite] = useState(null);      // l'événement natif, s'il arrive
  const [masquee, setMasquee] = useState(false);
  const [installee, setInstallee] = useState(estInstallee());

  // ── Le manifest du parcours, le temps que cet écran est affiché ───────────
  useEffect(() => {
    const lien = document.querySelector('link[rel="manifest"]');
    const precedent = lien?.getAttribute("href");
    if (lien) lien.setAttribute("href", MANIFEST_CHANTIER);
    return () => { if (lien && precedent) lien.setAttribute("href", precedent); };
  }, []);

  useEffect(() => {
    const surInvite = (e) => { e.preventDefault(); setInvite(e); };
    const surInstall = () => { setInstallee(true); setInvite(null); };
    window.addEventListener("beforeinstallprompt", surInvite);
    window.addEventListener("appinstalled", surInstall);
    return () => {
      window.removeEventListener("beforeinstallprompt", surInvite);
      window.removeEventListener("appinstalled", surInstall);
    };
  }, []);

  // ⛔ Déjà installée, ou fermée par l'utilisateur ⇒ rien. Ce n'est pas un blocage.
  if (installee || masquee) return null;

  const nav = navigateur();

  return (
    <div className="mx-4 mt-3 rounded-xl px-4 py-3" data-testid="invitation-pwa"
         style={{ background: "var(--oh-accent-bg)", color: "var(--oh-accent-text)",
                  border: "0.5px solid var(--oh-accent)" }}>
      <div className="flex items-start gap-3">
        <Download className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p style={{ fontWeight: 500 }}>Installer l'application</p>
          <p className="text-xs mt-0.5" style={{ color: "var(--oh-text-secondary)" }}>
            Pour travailler en plein écran, sans barre d'adresse, et retrouver le
            parcours depuis votre écran d'accueil.
          </p>

          {invite ? (
            // 🔑 Chemin NATIF : le navigateur sait installer, on lui demande.
            <Button className="mt-2" data-testid="pwa-installer" style={{ minHeight: 44 }}
                    onClick={async () => {
                      invite.prompt();
                      const choix = await invite.userChoice?.catch(() => null);
                      if (choix?.outcome === "accepted") setInstallee(true);
                      setInvite(null);
                    }}>
              Installer maintenant
            </Button>
          ) : (
            // ⚠️ Pas d'événement (cas de Samsung Internet) : on DONNE le geste exact.
            <p className="text-xs mt-2 flex items-start gap-1.5" data-testid="pwa-geste-manuel"
               style={{ color: "var(--oh-text)" }}>
              <Share2 className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>{GESTES[nav]}</span>
            </p>
          )}
        </div>
        <button type="button" onClick={() => setMasquee(true)} aria-label="Masquer"
                data-testid="pwa-masquer" style={{ minHeight: 44, minWidth: 44 }}>
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
